import type { ReactNode } from 'react';
import type { z } from 'zod';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { createPromiseWithResolvers } from '@/utils/promise';

type OrPromiseLike<T> = T | PromiseLike<T>;

interface ResolutionButton {
  label: string;
  resolution: string;
  hidden?: boolean;
  type?: 'permissive' | 'destructive' | 'informative';
}

export type IncomingDialogPrompt = {
  type: 'alert' | 'dialog' | 'action-sheet';
  title: ReactNode;
  content?: ReactNode;
  resolveButtonLabel?: string;
  rejectButtonLabel?: string;
  resolutionButtons?: Array<ResolutionButton>;
  meta?: {
    position: [number, number];
  };
};

export type IncomingFormPrompt = {
  type: 'form';
  title: ReactNode;
  content?: ReactNode;
  resolveButtonLabel?: string;
  rejectButtonLabel?: string;
  meta: {
    schema: z.AnyZodObject;
    defaultValues?: Record<string, unknown>;
  };
};

export type IncomingContextMenuPrompt = {
  type: 'context-menu';
  content: (resolvers: Prompt['resolvers']) => ReactNode;
  meta: {
    position: [number, number] | null | undefined;
  };
};

export type IncomingPrompt = {
  key: string;
  promise?: (resolution: OrPromiseLike<boolean | string | Record<string, unknown>>) => OrPromiseLike<unknown>;
} & (IncomingDialogPrompt | IncomingFormPrompt | IncomingContextMenuPrompt);

export type Prompt = IncomingPrompt & {
  isLoading: boolean;
  isPending: boolean;
  resolvers: {
    resolve: (value: OrPromiseLike<boolean | string | Record<string, unknown>>) => void;
    reject: () => void;
  };
};

interface PromptState {
  prompts: Record<string, Prompt>;
}

interface PromptActions {
  addPrompt: (prompt: IncomingPrompt) => Promise<boolean | string | Record<string, unknown>>;
  removePrompt: (key: string, shouldReject?: boolean) => void;
  clearPrompts: (shouldReject?: boolean) => void;
}

type PromptStore = PromptState & PromptActions;

export const usePrompterStore = create<PromptStore>()(
  immer((set, get) => ({
    prompts: {},

    addPrompt: async (prompt) => {
      const { promise, resolve, reject } =
        createPromiseWithResolvers<OrPromiseLike<boolean | string | Record<string, unknown>>>();

      set((state) => {
        state.prompts[prompt.key] = {
          ...prompt,
          isLoading: false,
          isPending: true,
          resolvers: { resolve, reject },
        };
      });

      return promise.then(async (value) => {
        if (prompt.promise && Boolean(value)) {
          set((state) => {
            const instance = state.prompts[prompt.key];
            if (instance) {
              instance.isPending = false;
              instance.isLoading = true;
            }
          });
          try {
            if (prompt.type === 'context-menu') prompt.promise(value);
            else await prompt.promise(value);
          } catch (e) {
            console.error(e);
            reject();
          }
        }

        get().removePrompt(prompt.key, false);
        return value;
      });
    },

    removePrompt: (key, shouldReject = true) => {
      set((state) => {
        if (state.prompts[key]) {
          if (shouldReject) state.prompts[key].resolvers.reject();
          delete state.prompts[key];
        }
      });
    },

    clearPrompts: (shouldReject = true) => {
      set((state) => {
        for (const key in state.prompts) {
          if (state.prompts[key]) {
            if (shouldReject) state.prompts[key].resolvers.reject();
            delete state.prompts[key];
          }
        }
      });
    },
  })),
);

export const makePrompt = usePrompterStore.getState().addPrompt;

export const clearPrompts = usePrompterStore.getState().clearPrompts;
